import { action, observable, runInAction, makeObservable, computed } from 'mobx'
import {
    getRightsholderUsers,
    getRightsholderUserToAbility,
    inviteRightsholderUser,
} from '@App/api/routes/rightsholder/rightsholderUser'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import RootStore from '@App/stores/root-store'
import { UserInfo, UsersList } from '@App/entities/user/user'
import { PaginationBodyUsers, PaginationInfo } from '@Entities/pagination'
import { ResultPage } from '@Entities/result-page'

export default class RightsholderUserStore {
    private rootStore: RootStore

    private _rightsHolderUsersForUser: UsersList | null

    private _rightsHolderUserToAbility: UserInfo | null

    private _rightsHolderUsers: ResultPage<UserInfo> | null

    private _rightsHolderUsersPagination: PaginationInfo | null

    private _rightsHolderUserInviteInfo: UserInfo | null

    constructor(rootStore: RootStore) {
        makeObservable<
            RightsholderUserStore,
            | '_rightsHolderUsersForUser'
            | '_rightsHolderUserToAbility'
            | '_rightsHolderUsers'
            | '_rightsHolderUserInviteInfo'
        >(this, {
            _rightsHolderUsersForUser: observable,
            _rightsHolderUserToAbility: observable,
            _rightsHolderUsers: observable,
            _rightsHolderUserInviteInfo: observable,
            permissions: computed,
            destroyRightsHolderInviteInfo: action,
            destroyRightsHolderUserToAbility: action,
            destroyRightsHolderUser: action,
            destroyRightsHolderUsers: action,
        })

        this.rootStore = rootStore
    }

    get rightsholderUsersForUser() {
        return this._rightsHolderUsersForUser
    }

    get rightsHolderUserToAbility() {
        return this._rightsHolderUserToAbility
    }

    get rightsholderUsers() {
        return this._rightsHolderUsers
    }

    get rightsHolderUsersPagination() {
        return this._rightsHolderUsersPagination
    }

    get rightsholderUserInviteInfo() {
        return this._rightsHolderUserInviteInfo
    }

    get permissions(): string[] {
        return this._rightsHolderUserToAbility?.permissions
            ? this._rightsHolderUserToAbility?.permissions?.map((perm) => perm.name)
            : []
    }

    public doesRHUserHavePermission(permission: string): boolean {
        return this.permissions.includes(permission)
    }

    public doesRHUserHavePermissionOrIsInternal(permission: string): boolean {
        return this.rootStore.userStore.isInternal || this.permissions.includes(permission)
    }

    public doesRHUserHaveOneOfThesePermission(requiredPermissions: string[]): boolean {
        return requiredPermissions.some((perm) => this.permissions.includes(perm))
    }

    public doesRHUserHaveOneOfThesePermissionOrIsInternal(requiredPermissions: string[]): boolean {
        let hasPermission = this.rootStore.userStore.isInternal
        if (!hasPermission) {
            hasPermission = requiredPermissions.some((perm) => this.permissions.includes(perm))
        }
        return hasPermission
    }

    public loadRightsholderUsers = async (pagination: Partial<PaginationBodyUsers>): Promise<void> => {
        const rhId = this.rootStore.rightsholderStore.currentRightsholderId
        const users = await getRightsholderUsers(rhId, pagination)
        runInAction(() => {
            this._rightsHolderUsers = users.data
            if (pagination && users.data) {
                this._rightsHolderUsersPagination = calcNumPages(
                    users.data.totalElements,
                    pagination.limit,
                    pagination.page
                )
            }
        })
    }

    public loadRightsholderUsersForUser = async (userId: string, body: Partial<PaginationBodyUsers>): Promise<void> => {
        const response = await getRightsholderUsers(this.rootStore.rightsholderStore.currentRightsholderId, body)
        const usersForUser: UsersList = {
            count: response.data.totalElements,
            users: response.data.content.filter((item: UserInfo) => item.exposedId !== userId).slice(0, body.limit),
        }
        runInAction(() => (this._rightsHolderUsersForUser = usersForUser))
    }

    public loadRightsholderUserToAbility = async (userId: string): Promise<void> => {
        const response = await getRightsholderUserToAbility(
            this.rootStore.rightsholderStore.currentRightsholderId,
            userId
        )
        runInAction(() => (this._rightsHolderUserToAbility = response.data))
    }

    public inviteRightsholderUser = async (data: Partial<UserInfo>): Promise<void> => {
        const response = await inviteRightsholderUser(this.rootStore.rightsholderStore.currentRightsholderId, data)
        runInAction(() => (this._rightsHolderUserInviteInfo = response.data))
    }

    public destroyRightsHolderInviteInfo(): void {
        this._rightsHolderUserInviteInfo = null
    }

    public destroyRightsHolderUserToAbility = (): void => {
        this._rightsHolderUserToAbility = null
    }

    public destroyRightsHolderUser = (): void => {
        this._rightsHolderUsersForUser = null
    }

    public destroyRightsHolderUsers = (): void => {
        this._rightsHolderUsers = null
        this._rightsHolderUsersPagination = null
    }
}
